import React from 'react';
import {
  Text,
  Tag,
  HStack,
  Icon,
  Link,
  Wrap,
  Tooltip,
  Box,
  VStack,
  ListItem,
  UnorderedList,
} from '@wegroup/design-system';
import {
  InsuranceTermsBotResource,
  InsuranceTermsBotResourceSection,
} from '../../api/postInsuranceTermsBotChat';
import { useTranslation } from 'react-i18next';
import useTracking from '../../../../../../common/src/hooks/tracking/useTracking';
import { truncate, uniqBy } from 'lodash';
import { formatDateTime } from '../../../../../../common/src/utils/dateUtils';
import i18n from '../../../../../../../i18n';

interface Props {
  resources: InsuranceTermsBotResource[];
}

const LouiseChatResources: React.FC<Props> = ({ resources }) => {
  const { t } = useTranslation();
  const filteredResources = uniqBy(
    resources,
    (resource) => resource.name && resource.section.page,
  );

  return (
    <VStack align="flex-start">
      <Text
        // Using fontsize instead of textstyle because of a bug with the textstyle for CSS print
        fontSize="sm"
        flexShrink={0}
        sx={{
          '@media print': {
            // This is needed to fix an issue with font sizing for CSS print
            fontSize: '12px !important',
          },
        }}
      >
        {t('TOOLKIT_INSURANCE-TERMS-BOT_ANSWER_READ-MORE-AT')}:
      </Text>
      <Wrap
        sx={{
          '@media print': {
            display: 'none',
          },
        }}
      >
        {[...filteredResources].map((resource) => (
          <ResourceTag
            {...resource}
            key={
              resource.link + resource.section.reference + resource.section.page
            }
          />
        ))}
      </Wrap>
      <UnorderedList
        pl="4"
        spacing="1"
        display="none"
        sx={{
          '@media print': {
            display: 'block',
          },
        }}
      >
        {[...filteredResources].map((resource) => (
          <PrintResourceTag
            {...resource}
            key={
              resource.link + resource.section.reference + resource.section.page
            }
          />
        ))}
      </UnorderedList>
    </VStack>
  );
};

const getDocumentTitle = (
  name: string,
  section: InsuranceTermsBotResourceSection,
) => {
  return `${section.title || name} ${
    section.page ? `(${i18n.t('PAGE')} ${section.page})` : ''
  }`;
};

const ResourceTag: React.FC<InsuranceTermsBotResource> = ({
  section,
  link,
  icon_key,
  name,
  created_at,
}) => {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();

  const documentTitle = getDocumentTitle(name, section);

  return (
    <Tag
      shrink={0}
      textStyle="extraSmallBody"
      size="sm"
      as={Link}
      href={section.page ? `${link}#page=${section.page}` : link}
      isExternal
      onClick={() => trackEvent('Open document from Louise GPT answer')}
    >
      <HStack>
        <Icon name={icon_key} boxSize={4} />
        <Text as="span">
          {truncate(documentTitle, {
            length: 128,
            separator: ' ',
          })}
        </Text>
        <Tooltip
          maxW="96"
          label={
            <VStack align="flex-start">
              <Text>{documentTitle}</Text>
              <Text>
                -{' '}
                {t('TOOLKIT_INSURANCE-TERMS-BOT_ANSWER_RESOURCE-INFO', {
                  date: formatDateTime(created_at),
                })}
              </Text>
            </VStack>
          }
          shouldWrapChildren
        >
          {/* Through negative margin we try to negate the viewbox which is too large */}
          <Box mt="-1" mb="-1" mr="-1">
            <Icon name="GeneralStatusInfo" />
          </Box>
        </Tooltip>
      </HStack>
    </Tag>
  );
};

const PrintResourceTag: React.FC<InsuranceTermsBotResource> = ({
  name,
  link,
  section,
}) => {
  const documentTitle = getDocumentTitle(name, section);
  return (
    <ListItem fontSize="xs" lineHeight="1.2">
      <Text>{documentTitle}:</Text>{' '}
      <Link href={link} isExternal>
        {link}
      </Link>
    </ListItem>
  );
};

export default LouiseChatResources;
